import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, apiErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { Nav, Footer } from "../components/Nav";
import { VideoPreview } from "../components/VideoPreview";
import { toast } from "sonner";

function parseDurationSeconds(str) {
  if (!str) return 0;
  const [m, s] = str.split(":").map(Number);
  return (m || 0) * 60 + (s || 0);
}
function formatTotal(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
function formatModuleDuration(lessons) {
  const s = (lessons || []).reduce((acc, l) => acc + parseDurationSeconds(l.duration_display), 0);
  if (!s) return "";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export default function CourseDetail() {
  const [course, setCourse] = useState(null);
  const [currency, setCurrency] = useState("inr");
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/course").then((r) => setCourse(r.data));
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      if (!tz.includes("Kolkata") && !tz.includes("Calcutta")) setCurrency("usd");
    } catch { /* noop */ }
  }, []);

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const enroll = async () => {
    if (!user) { nav("/login?next=/course"); return; }
    if (user.role === "admin") { toast.info("Admins cannot enroll"); return; }
    setLoading(true);
    try {
      if (currency === "inr") {
        // Razorpay flow
        const ok = await loadRazorpayScript();
        if (!ok) throw new Error("Failed to load Razorpay");
        const { data } = await api.post("/payments/razorpay/create-order", {
          course_id: "indian_fashion_illustration",
        });
        const options = {
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          order_id: data.order_id,
          name: "Lata Illustrator",
          description: "Indian Fashion Illustration — The Complete Course",
          prefill: { name: user.name, email: user.email },
          theme: { color: "#D4AF37" },
          handler: async (resp) => {
            try {
              const { data } = await api.post("/payments/razorpay/verify", {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              if (data?.status === "ok") {
                window.location.href = `/payment/success?session_id=${resp.razorpay_order_id}`;
              } else {
                toast.error("Payment could not be verified. No access granted.");
                setLoading(false);
              }
            } catch (e) {
              toast.error(apiErrorMessage(e, "Payment verification failed. No access granted."));
              setLoading(false);
            }
          },
          modal: { ondismiss: () => setLoading(false) },
        };
        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", (r) => toast.error(r?.error?.description || "Payment failed"));
        rzp.open();
        return; // don't unset loading; handler / dismiss will
      }
      // Stripe (USD) flow — unchanged
      const { data } = await api.post("/payments/checkout", {
        course_id: "indian_fashion_illustration",
        currency,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErrorMessage(e, "Checkout failed"));
    } finally { if (currency !== "inr") setLoading(false); }
  };

  if (!course) return <div className="p-10">Loading...</div>;
  // Prefer live prices from settings (admin-editable); fallback to server-side course pricing
  const priceInr = settings?.price_inr ?? course.pricing.inr;
  const priceUsd = settings?.price_usd ?? course.pricing.usd;
  const price = currency === "inr" ? priceInr : priceUsd;
  const displayTitle = settings?.course_title || course.title;
  const displayDesc = settings?.course_description || course.description;
  const displayLearn = settings?.course_what_you_learn?.length ? settings.course_what_you_learn : course.what_you_learn;
  // Total course duration (sum of all lesson duration_display strings)
  const totalSeconds = (course.modules || []).flatMap(m => m.lessons || [])
    .reduce((acc, l) => acc + parseDurationSeconds(l.duration_display), 0);
  const totalDurationStr = formatTotal(totalSeconds);
  const totalLessons = (course.modules || []).reduce((n, m) => n + (m.lessons?.length || 0), 0);

  return (
    <div className="bg-[#FAFAFA]">
      <Nav />

      {/* COURSE PREVIEW — Bunny Stream */}
      <VideoPreview
        variant="dark"
        eyebrow="Course Preview"
        headline={<>See the <span className="font-serif-italic italic text-[#D4AF37]">Course</span><br />before you enrol.</>}
        body="A short film through the syllabus — the drape studies, the ornament work, the silhouette. Take a look inside the studio."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-24 grid grid-cols-1 md:grid-cols-12 gap-16 items-start">
        <div className="md:col-span-7">
          <p className="overline mb-8"><span className="gold-line mr-3" />Course</p>
          <h1 className="font-serif-display font-light text-5xl md:text-6xl leading-[1.05]" data-testid="course-title">
            {displayTitle.split("—")[0]}
            <br />
            <span className="font-serif-italic italic text-[#D4AF37]">— {displayTitle.split("—")[1]?.trim() || ""}</span>
          </h1>
          <p className="mt-8 leading-relaxed text-[#3a3a3a] max-w-lg">{displayDesc}</p>

          <div className="mt-16">
            <p className="overline mb-6">What you'll learn</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
              {displayLearn.map((it, i) => (
                <div key={i} className="flex gap-3 items-start border-b border-black/5 py-3">
                  <span className="text-[#D4AF37] font-serif-italic italic">{String(i+1).padStart(2,'0')}</span>
                  <span className="text-sm">{it}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-16">
            <p className="overline mb-6">Modules</p>
            <div className="space-y-0">
              {course.modules.map((m, i) => (
                <div key={m.id} className="border-b border-black/10 py-6" data-testid={`module-${i}`}>
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2 md:col-span-1"><p className="font-serif-italic italic text-[#D4AF37] text-2xl">{String(i+1).padStart(2,'0')}</p></div>
                    <div className="col-span-10 md:col-span-11">
                      <h3 className="font-serif-display text-2xl leading-tight">{m.title}</h3>
                      <p className="text-sm text-[#6B6B6B] mt-2">{m.description}</p>
                      <p className="overline mt-3">{m.lessons?.length || 0} lesson{m.lessons?.length !== 1 && 's'} · {formatModuleDuration(m.lessons)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* PRICING CARD */}
        <aside className="md:col-span-5 md:sticky md:top-28">
          <div className="border border-black/15 bg-white p-8 md:p-10">
            <p className="overline">Enrollment</p>
            <div className="mt-4 flex items-baseline gap-3">
              <p className="font-serif-display text-2xl text-black/40 line-through" data-testid="course-original-price">
                {currency === 'inr' ? '₹19,999' : '$240'}
              </p>
              <span className="text-[10px] tracking-widest uppercase text-[#D4AF37] border border-[#D4AF37] px-2 py-1" data-testid="course-discount-badge">50% OFF</span>
            </div>
            <p className="font-serif-display text-5xl mt-2" data-testid="course-price">
              {currency === 'inr' ? '₹' : '$'}{price.toLocaleString()}
            </p>
            <p className="text-xs mt-2 text-[#D4AF37] italic font-serif-italic" data-testid="early-bird-note">Early Bird Offer — limited seats</p>
            <p className="overline mt-2">One-time · 3 months access</p>

            <div className="mt-8 flex border border-black/15">
              <button
                data-testid="currency-inr"
                onClick={() => setCurrency('inr')}
                className={`flex-1 py-3 text-xs uppercase tracking-widest ${currency==='inr' ? 'bg-black text-white' : 'text-black'}`}
              >INR ₹</button>
              <button
                data-testid="currency-usd"
                onClick={() => setCurrency('usd')}
                className={`flex-1 py-3 text-xs uppercase tracking-widest ${currency==='usd' ? 'bg-black text-white' : 'text-black'}`}
              >USD $</button>
            </div>

            <button
              onClick={enroll}
              disabled={loading}
              data-testid="course-enroll-button"
              className="btn-editorial w-full mt-6"
            >{loading ? 'Redirecting…' : 'Enroll Now'}</button>

            {settings?.course_outline_pdf_path && (
              <a
                href={`${API}/media/course-outline`}
                data-testid="course-outline-download"
                className="btn-editorial-outline w-full mt-3 block"
              >Download Course Outline</a>
            )}

            <ul className="mt-8 space-y-3 text-sm text-[#3a3a3a]">
              <li className="flex justify-between border-b border-black/5 pb-2"><span>~2 hours of studio video</span><span className="text-[#D4AF37]">✓</span></li>
              <li className="flex justify-between border-b border-black/5 pb-2"><span>3 months of access</span><span className="text-[#D4AF37]">✓</span></li>
              <li className="flex justify-between border-b border-black/5 pb-2"><span>Taught in simple, clear English</span><span className="text-[#D4AF37]">✓</span></li>
              <li className="flex justify-between border-b border-black/5 pb-2"><span>Downloadable worksheets</span><span className="text-[#D4AF37]">✓</span></li>
              <li className="flex justify-between border-b border-black/5 pb-2"><span>Signed certificate</span><span className="text-[#D4AF37]">✓</span></li>
              <li className="flex justify-between"><span>Instructor reviewed</span><span className="text-[#D4AF37]">✓</span></li>
            </ul>
          </div>
        </aside>
      </section>
      <Footer />
    </div>
  );
}
